import { Action, ActionReducer, createReducer, MetaReducer, on } from '@ngrx/store'
import {
    initializeMessages,
    newAccommodationData,
    newChatMessage,
    messagesRead,
    newSaleData,
    newMessage,
    prependOlderMessages,
    profileUpdateSuccess,
    setCorpMemberProfileData,
    updateMessages,
    updateReadReceipts,
    initializePostsHistory,
    deletePostedPost,
    postRestored,
    loadAllItemsSuccess,
    ppasLoaded,
    morePpasLoaded,
    ppaAdded,
    ppaReviewStatsUpdated,
    savedSalesLoaded,
    saleSaveChanged,
    savedAccommodationsLoaded,
    accommodationSaveChanged,
    saleSoldChanged,
} from '../actions/corp-member.actions'
import { chatMessageSent } from '../actions/chat-outbox.actions'
import {
    AppState,
    CorpMemberState,
    FeedDataType,
    AppMessages,
    initialCorpMemberState,
    initialState,
    ChatMessage,
    AppMessageValue,
    SaleType,
    AccommodationType,
    AppFeedState,
    PostHistoryState,
    PpasState,
} from '../app.state'
import { selectFeatureMessages } from '../selectors/corp.selectors'
import { CallerService } from '../../services/caller.service'

export const corpMemberReducer = createReducer(
    initialCorpMemberState, // or initialState.corper // initial state should be the corper from the whole app state
    on(setCorpMemberProfileData, (state: CorpMemberState, { data }) => {
        /**
         * update local storage
         * 
         * on mobile, this doesn't update fast enough so auth fails on /home page
         * fix is to make the update before the dispatching the event in login component
         */
        try {
            localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(data));
        } catch (error) {
            console.log('st err set', error)
            // todo: show notification??
        }

        return data
    }),

    /**
     * Update part of the corper state
     */
    on(profileUpdateSuccess, (state: CorpMemberState, { data }) => {
        const _body = {...state, ...data}
        // update local storage
        try {
            localStorage.setItem(CallerService.LOCAL_STORAGE_DATA_KEY, JSON.stringify(_body));
        } catch (error) {
            console.log('st err update', error);
        }
        return _body
    }),
)

/**
 * Merges incoming items on top of an existing feed map: incoming entries are
 * inserted first (so they win both the value AND the front-of-map position -
 * Map keeps a key's original insertion position even when its value is later
 * overwritten, so a colliding key can't be allowed to appear in both spreads),
 * then whatever existing entries aren't superseded are appended after them.
 */
function mergeFeedItems<T extends { id?: number | string }>(
    existing: Map<number | string, T>,
    incoming: Array<T | any> | undefined,
): Map<number | string, T> {
    const incomingIds = new Set((incoming ?? []).map((item) => item?.id))
    return new Map([
        ...(incoming ?? []).map((item) => [item?.id, item] as const),
        ...[...existing].filter(([id]) => !incomingIds.has(id)),
    ])
}

export const feedReducer = createReducer(
    initialState.feed, // initial state should be the feed from the whole app state
    on(newSaleData, (feedState: FeedDataType, { data }) => {
        console.log(feedState, 'just got new sale', data)
        // @todo: should we use browser cache to so some work?
        return {
            ...feedState,
            sales: mergeFeedItems(feedState.sales, data),
        }
    }),
    on(newAccommodationData, (feedState: FeedDataType, { data }) => {
        console.log(feedState, 'just got new accommodation', data)
        // TODO: should we use browser cache to so some work?
        return {
            ...feedState,
            accommodations: mergeFeedItems(feedState.accommodations, data),
        }
    }),
    on(loadAllItemsSuccess, (feedState: AppFeedState, { sales, accommodations, fetchedAt }) => ({
        ...feedState,
        sales: mergeFeedItems(feedState.sales, sales),
        accommodations: mergeFeedItems(feedState.accommodations, accommodations),
        lastFetchedAt: fetchedAt,
    })),
    on(savedSalesLoaded, (feedState: AppFeedState, { ids }) => ({
        ...feedState,
        savedSaleIds: ids ? new Set(ids) : null,
    })),
    on(saleSaveChanged, (feedState: AppFeedState, { id, saved }) => {
        const savedSaleIds = new Set(feedState.savedSaleIds ?? [])
        if (saved) savedSaleIds.add(id)
        else savedSaleIds.delete(id)
        return { ...feedState, savedSaleIds }
    }),
    on(savedAccommodationsLoaded, (feedState: AppFeedState, { ids }) => ({
        ...feedState,
        savedAccommodationIds: ids ? new Set(ids) : null,
    })),
    on(accommodationSaveChanged, (feedState: AppFeedState, { id, saved }) => {
        const savedAccommodationIds = new Set(feedState.savedAccommodationIds ?? [])
        if (saved) savedAccommodationIds.add(id)
        else savedAccommodationIds.delete(id)
        return { ...feedState, savedAccommodationIds }
    }),
    // Post history's `${_type}_${id}` key: a deleted post leaves the feed too.
    on(deletePostedPost, (feedState: AppFeedState, { id }) => {
        const [type, postId] = id.split('_')
        if (type === 'sale' && feedState.sales.has(Number(postId))) {
            const sales = new Map(feedState.sales)
            sales.delete(Number(postId))
            return { ...feedState, sales }
        }
        if (type === 'accommodation' && feedState.accommodations.has(Number(postId))) {
            const accommodations = new Map(feedState.accommodations)
            accommodations.delete(Number(postId))
            return { ...feedState, accommodations }
        }
        return feedState
    }),
    on(postRestored, (feedState: AppFeedState, { post }) =>
        post._type === 'sale'
            ? { ...feedState, sales: mergeFeedItems(feedState.sales, [post]) }
            : { ...feedState, accommodations: mergeFeedItems(feedState.accommodations, [post]) },
    ),
    on(saleSoldChanged, (feedState: AppFeedState, { id, sold_at }) => {
        const sale = feedState.sales.get(id)
        if (!sale) return feedState
        // A copy, so the sale keeps its place in the Map.
        const sales = new Map(feedState.sales)
        sales.set(id, { ...sale, sold_at })
        return { ...feedState, sales }
    }),
)

/**
 * The logged-in corp member's chat identity, read from the same localStorage blob
 * CallerService hydrates from. Chats now identify participants by slug_id rather than
 * numeric id — falls back to id (stringified) for the rare case a cached profile predates
 * slug_id.
 */
function getStoredChatIdentity(): string | undefined {
    const corper = JSON.parse(new String(localStorage.getItem(CallerService.LOCAL_STORAGE_DATA_KEY)).toString())
    return corper?.slug_id ?? (corper?.id !== undefined ? String(corper.id) : undefined)
}

/**
 * `chat`, a saved message, added to its room. The sender gets each of their messages twice - in
 * its send's ack and in the room broadcast - so one that's already there changes nothing.
 */
function addSavedMessage(state: AppMessages, chat: ChatMessage): AppMessages {
    const roomChat = state.get(chat.room)
    const texts = roomChat?.texts ?? []
    if (texts.some(t => t.id === chat.id)) return state

    return new Map([
        ...state,
        [
            chat.room,
            {
                ...roomChat,
                /**
                 * increment unread message (if they have the chat open, update the count to 0)
                 *
                 * if the message is not from the current user, increment unread_messages
                 */
                unread_messages:
                    chat.message_from !== getStoredChatIdentity()
                        ? Number(roomChat?.unread_messages || 0) + 1
                        : (roomChat?.unread_messages || 0),
                texts: [...texts, chat],
            },
        ],
    ])
}

/**
 * If there's no new message before; initialize a new one.
 * TODO: update local storage with new states?
 */
export const messagesReducer = createReducer(
    initialState.messages, // initial state should be the messages from the whole app state
    on(initializeMessages, (state, { type, ...data }) => {
        return new Map(Object.entries(data))
    }),
    on(updateMessages, (state: AppMessages, { type, ...data }) => {
        if (typeof data === 'object') {
            const newData = new Map<string, AppMessageValue>(Object.entries(data))

            // Drop any local ghost rooms (no messages yet) whose user pair matches a room
            // returned by the server. This prevents a FE-generated UUID from shadowing the
            // canonical server UUID after navigation to the messages page.
            const filteredState = new Map(state)
            for (const [localKey, localChat] of filteredState) {
                if (localChat.texts.length > 0) continue
                for (const serverChat of newData.values()) {
                    const sameUsers =
                        (localChat.recipient_id === serverChat.recipient_id && localChat.initiator_id === serverChat.initiator_id) ||
                        (localChat.recipient_id === serverChat.initiator_id && localChat.initiator_id === serverChat.recipient_id)
                    if (sameUsers) {
                        filteredState.delete(localKey)
                        break
                    }
                }
            }

            // Preserve locally-zeroed unread counts to avoid overwriting a mark-read HTTP
            // call that completed locally but whose DB update hasn't been reflected in this
            // resolver response yet (race condition on slow connections).
            const mergedData = new Map<string, AppMessageValue>()
            for (const [room, serverChat] of newData) {
                const localChat = filteredState.get(room)
                if (localChat && localChat.unread_messages === 0 && serverChat.unread_messages > 0) {
                    mergedData.set(room, { ...serverChat, unread_messages: 0 })
                } else {
                    mergedData.set(room, serverChat)
                }
            }

            return new Map([...filteredState, ...mergedData])
        }
        return state
    }),

    on(
        newMessage,
        (state: AppMessages, { 
            room, recipient_id, recipient_state_code, 
            initiator_state_code, initiator_name, 
            initiator_id, recipient_name,
            chat_display_name, chat_display_state_code,
        }) =>
        {
            /**
             * @todo: this is not the best way to do this, but it works for now.
             * We should probably use a more efficient way to check if the chat already exists.
             * we should use the recipient_id and the initiator_id as the key of the map, and to check if the chat already exists.
             */
            // find if there's existing chat, find it.
            for (const [key, value] of state) {
                const hasExistingChat =
                    (value.recipient_id === recipient_id && value.initiator_id === initiator_id) ||
                    (value.recipient_id === initiator_id && value.initiator_id === recipient_id)
                if (hasExistingChat) {
                    console.log('found existing chat', key)
                    // we can't sort or order items in map, stays as they were inserted
                    const newState = new Map([...state])
                    newState.delete(key)
                    // so it goes to the beginning.
                    newState.set(key, value)

                    // TODO: best way to make a copy???
                    return newState
                }
            }

            const new_chat_room = window?.crypto?.randomUUID?.() ?? Math.random().toString(36).substring(2)
            return new Map([
                ...state,
                [
                    new_chat_room,
                    {
                        texts: [],
                        unread_messages: 0,
                        recipient_state_code,
                        initiator_state_code,
                        recipient_name,
                        initiator_name,
                        recipient_id,
                        initiator_id,
                        chat_display_name, chat_display_state_code,
                    },
                ],
            ])
        }
    ),
    // The sender's own unsent messages aren't here - they're in the chat outbox (see
    // selectMessagesWithOutbox), which drops each one once its saved copy arrives.
    on(newChatMessage, (state: AppMessages, chatMessage: ChatMessage) => addSavedMessage(state, chatMessage)),

    // ✓ -> ✓✓ on what was sent to the reader. Unchanged state when there's nothing new to mark.
    on(messagesRead, (state: AppMessages, { room, reader, read_at }) => {
        const chat = state.get(room)
        if (!chat?.texts.some((t) => t.message_to === reader && !t.time_read)) return state
        const texts = chat.texts.map((t) => (t.message_to === reader && !t.time_read ? { ...t, time_read: read_at } : t))
        return new Map(state).set(room, { ...chat, texts })
    }),
    on(chatMessageSent, (state: AppMessages, { chat }) => (chat ? addSavedMessage(state, chat) : state)),

    on(
        prependOlderMessages,
        (state: AppMessages, { room, messages }) => {
            const existingRoom = state.get(room)
            if (!existingRoom) return state
            const existingIds = new Set(existingRoom.texts.map(t => t.id))
            const deduped = messages.filter(m => !existingIds.has(m.id))
            return new Map([
                ...state,
                [room, {
                    ...existingRoom,
                    texts: [...deduped, ...existingRoom.texts],
                }]
            ])
        }
    ),

    on(
        updateReadReceipts,
        (state: AppMessages, data) => {
            const _corp_member_id = getStoredChatIdentity()
            return new Map([
                ...state,
                [
                    data.room,
                    {
                        ...state.get(data.room),
                        // main update
                        unread_messages: 0,
                        /**
                         * only update read_by_to if it's same with recipient_id === _corp_member_id
                         * 
                         * this is kinda too much of a computation??? maybe
                         */
                        texts: [...(state.get(data.room)?.texts ?? []).map(
                            (t: ChatMessage) => {
                                if (t.message_to === _corp_member_id) {
                                    return {...t, read_by_to: true}
                                }
                                return t
                            }
                        )]
                    },
                ],
            ])
        },
    ),
)

export const postHistoryReducer = createReducer(
    initialState.posted_items,
    on(initializePostsHistory, (state: PostHistoryState, { data }) => {
        // console.log('das state', state)
        // console.log('continue', data)
        return new Map([...state, ...data])
    }),
    on(saleSoldChanged, (state: PostHistoryState, { id, sold_at }) => {
        const key = `sale_${id}`
        const sale = state.get(key)
        if (!sale) return state
        const newState = new Map(state)
        newState.set(key, { ...sale, sold_at } as SaleType)
        return newState
    }),
    on(postRestored, (state: PostHistoryState, { post }) => new Map([...state, [`${post._type}_${post.id}`, post]])),
    on(deletePostedPost, (state: PostHistoryState, { id }) => {
        const newState = new Map([...state])
        newState.delete(id)
        
        // TODO: best way to make a copy???
        return newState
    }),
)

export const ppasReducer = createReducer(
    initialState.ppas,
    on(ppasLoaded, (_state: PpasState, { ppas, total }) => ({ list: ppas, total })),
    // Without the ones shown already - a PPA added since the last page shifts the next one along.
    on(morePpasLoaded, (state: PpasState, { ppas, total }) => {
        const shown = new Set(state.list.map((ppa) => ppa.id))
        return { list: [...state.list, ...ppas.filter((ppa) => !shown.has(ppa.id))], total }
    }),
    on(ppaAdded, (state: PpasState, { ppa }) => ({ list: [ppa, ...state.list], total: state.total + 1 })),
    on(ppaReviewStatsUpdated, (state: PpasState, { id, stats }) => ({
        ...state,
        list: state.list.map((ppa) => (ppa.id === id ? { ...ppa, ...stats } : ppa)),
    })),
)

// Logger Meta-Reducer (from claude.ai)
export function logger(reducer: ActionReducer<any>): ActionReducer<any> {
    return (state, action) => {
        // uncomment to debug ngrx store
        // console.log('-state', state);
        // console.log('-action', action);

        return reducer(state, action);
    };
}

// Add this to your meta-reducers array (from claude.ai)
export const metaReducers: MetaReducer<any>[] = [logger];